import React, { useEffect, useState } from "react";
import { LogOut, Mail, Phone, Calendar, Package, Palette, DollarSign, Image as ImageIcon, X, RefreshCcw, Sparkles, MessageCircle, CheckCircle2, Link2, Copy } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import GalleryManager from "@/components/GalleryManager";
import DiscountManager from "@/components/DiscountManager";

function fmt(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

export default function AdminDashboard() {
  const { admin, logout } = useAuth();
  const [tab, setTab] = useState("inquiries");
  const [inquiries, setInquiries] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);
  const [photoData, setPhotoData] = useState(null);
  const [photoLoading, setPhotoLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [i, c] = await Promise.all([api.get("/inquiries"), api.get("/contacts")]);
      setInquiries(i.data || []);
      setContacts(c.data || []);
    } catch (e) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openInquiry = async (row) => {
    setOpen(row);
    setPhotoData(null);
    if (row.has_photo) {
      setPhotoLoading(true);
      try {
        const { data } = await api.get(`/inquiries/${row.id}/photo`);
        setPhotoData(data);
      } catch (_) {
        toast.error("Could not load photo");
      } finally { setPhotoLoading(false); }
    }
  };

  return (
    <div className="min-h-screen bg-sakura-50" data-testid="page-admin">
      <div className="max-w-7xl mx-auto px-5 md:px-10 py-10">
        <header className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <span className="chip">Admin</span>
            <h1 className="mt-2 font-serif text-3xl md:text-4xl text-sakura-ink">Hi {admin?.name || "Kaitlyn"} 🌸</h1>
            <p className="text-sakura-soft text-sm">Logged in as {admin?.email}</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={load} className="btn-ghost" data-testid="admin-refresh"><RefreshCcw className="h-4 w-4" /> Refresh</button>
            <button onClick={logout} className="btn-secondary" data-testid="admin-logout"><LogOut className="h-4 w-4" /> Sign out</button>
          </div>
        </header>

        <div className="mt-8 grid md:grid-cols-3 gap-4">
          <Stat label="Order Inquiries" value={inquiries.length} icon={Sparkles} />
          <Stat label="Contact Messages" value={contacts.length} icon={MessageCircle} />
          <Stat label="With Photos" value={inquiries.filter(i => i.has_photo).length} icon={ImageIcon} />
        </div>

        <div className="mt-8 flex gap-2" data-testid="admin-tabs">
          <button onClick={() => setTab("inquiries")} className={`px-4 py-2 rounded-full text-sm font-semibold ${tab === "inquiries" ? "bg-sakura-400 text-white shadow-sakura" : "bg-white border border-sakura-200 text-sakura-ink"}`} data-testid="admin-tab-inquiries">Inquiries</button>
          <button onClick={() => setTab("contacts")} className={`px-4 py-2 rounded-full text-sm font-semibold ${tab === "contacts" ? "bg-sakura-400 text-white shadow-sakura" : "bg-white border border-sakura-200 text-sakura-ink"}`} data-testid="admin-tab-contacts">Contacts</button>
        </div>

        <div className="mt-5 card-soft overflow-hidden">
          {loading ? (
            <div className="p-10 text-center text-sakura-soft">Loading…</div>
          ) : tab === "inquiries" ? (
            inquiries.length === 0 ? (
              <div className="p-10 text-center text-sakura-soft">No inquiries yet. They'll appear here as they come in.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="inquiries-table">
                  <thead className="bg-sakura-100 text-sakura-ink">
                    <tr className="text-left">
                      <th className="p-4">When</th>
                      <th className="p-4">Name</th>
                      <th className="p-4">Event</th>
                      <th className="p-4">Date</th>
                      <th className="p-4">Budget</th>
                      <th className="p-4">Deposit</th>
                      <th className="p-4"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {inquiries.map((r) => (
                      <tr key={r.id} className="border-t border-sakura-200 hover:bg-sakura-100/50">
                        <td className="p-4 text-sakura-soft whitespace-nowrap">{fmt(r.created_at)}</td>
                        <td className="p-4 font-semibold text-sakura-ink">{r.name}<div className="text-xs font-normal text-sakura-soft">{r.email}</div></td>
                        <td className="p-4 text-sakura-ink">{r.event_type || "—"}</td>
                        <td className="p-4 text-sakura-ink">{r.event_date || "—"}</td>
                        <td className="p-4 text-sakura-ink">{r.budget_range || "—"}</td>
                        <td className="p-4 text-sakura-ink">{r.deposit_paid ? (
                          <span className="inline-flex items-center gap-1 text-xs font-semibold bg-green-100 text-green-700 rounded-full px-2 py-1"><CheckCircle2 className="h-3 w-3" /> Paid{r.deposit_amount ? ` $${Number(r.deposit_amount).toFixed(0)}` : ""}</span>
                        ) : (
                          <span className="text-xs text-sakura-soft">—</span>
                        )}</td>
                        <td className="p-4 text-right">
                          <button onClick={() => openInquiry(r)} className="btn-ghost" data-testid={`inquiry-view-${r.id}`}>View</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          ) : (
            contacts.length === 0 ? (
              <div className="p-10 text-center text-sakura-soft">No contact messages yet.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="contacts-table">
                  <thead className="bg-sakura-100 text-sakura-ink">
                    <tr className="text-left"><th className="p-4">When</th><th className="p-4">Name</th><th className="p-4">Email</th><th className="p-4">Message</th></tr>
                  </thead>
                  <tbody>
                    {contacts.map((r) => (
                      <tr key={r.id} className="border-t border-sakura-200 align-top">
                        <td className="p-4 text-sakura-soft whitespace-nowrap">{fmt(r.created_at)}</td>
                        <td className="p-4 font-semibold text-sakura-ink">{r.name}</td>
                        <td className="p-4 text-sakura-ink">{r.email}</td>
                        <td className="p-4 text-sakura-ink max-w-md whitespace-pre-wrap">{r.message}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          )}
        </div>

        <DiscountManager />
        <GalleryManager />
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-sakura-ink/70 backdrop-blur p-4 grid place-items-center" onClick={() => setOpen(null)} data-testid="inquiry-detail-modal">
          <div className="bg-white rounded-3xl max-w-2xl w-full max-h-[85vh] overflow-auto shadow-sakuraStrong" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-sakura-200 p-5 flex items-center justify-between">
              <div>
                <h2 className="font-serif text-2xl text-sakura-ink">{open.name}</h2>
                <p className="text-sm text-sakura-soft">{fmt(open.created_at)}</p>
              </div>
              <button onClick={() => setOpen(null)} aria-label="Close" className="h-9 w-9 grid place-items-center rounded-full bg-sakura-100" data-testid="inquiry-detail-close"><X className="h-4 w-4" /></button>
            </div>
            <div className="p-6 grid sm:grid-cols-2 gap-3 text-sm">
              <Row icon={Mail} label="Email" value={<a href={`mailto:${open.email}`} className="text-sakura-500 underline">{open.email}</a>} />
              <Row icon={Phone} label="Phone" value={open.phone || "—"} />
              <Row icon={Calendar} label="Event date" value={open.event_date || "—"} />
              <Row icon={Package} label="Pickup/Delivery" value={open.delivery_preference || "—"} />
              <Row icon={Sparkles} label="Event type" value={open.event_type || "—"} />
              <Row icon={DollarSign} label="Budget" value={open.budget_range || "—"} />
              <Row icon={Package} label="Treats" value={open.treats_wanted || "—"} />
              <Row icon={Package} label="Quantity" value={open.quantity || "—"} />
              <Row icon={Palette} label="Theme/Colors" value={open.theme_colors || "—"} />
            </div>
            {open.notes && (
              <div className="px-6 pb-4">
                <div className="text-xs font-semibold uppercase tracking-wider text-sakura-500 mb-1">Notes</div>
                <div className="rounded-2xl bg-sakura-50 border border-sakura-200 p-4 text-sakura-ink whitespace-pre-wrap">{open.notes}</div>
              </div>
            )}

            <div className="px-6 pb-4">
              <div className="text-xs font-semibold uppercase tracking-wider text-sakura-500 mb-2 flex items-center gap-2"><Link2 className="h-3.5 w-3.5" /> Payment link for this customer</div>
              {(() => {
                const payLink = `${window.location.origin}/pay?inquiry=${open.id}`;
                return (
                  <div className="rounded-2xl bg-sakura-50 border border-sakura-200 p-3 flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                    <code className="flex-1 text-xs text-sakura-ink break-all bg-white rounded-xl px-3 py-2 border border-sakura-200">{payLink}</code>
                    <button
                      type="button"
                      onClick={() => { navigator.clipboard.writeText(payLink); toast.success("Payment link copied — paste into your reply email"); }}
                      className="btn-primary text-sm"
                      data-testid="inquiry-copy-pay-link"
                    >
                      <Copy className="h-3.5 w-3.5" /> Copy
                    </button>
                  </div>
                );
              })()}
              <p className="mt-2 text-[11px] text-sakura-soft">Send this link to the customer after you've confirmed their quote. The customer enters the amount (full payment under $30, 50% deposit over $30).</p>
            </div>
            {open.has_photo && (
              <div className="px-6 pb-6">
                <div className="text-xs font-semibold uppercase tracking-wider text-sakura-500 mb-2 flex items-center gap-2"><ImageIcon className="h-3.5 w-3.5" /> Inspiration photo {open.inspiration_photo_name && `· ${open.inspiration_photo_name}`}</div>
                {photoLoading ? (
                  <div className="rounded-2xl bg-sakura-50 border border-sakura-200 p-8 text-center text-sakura-soft">Loading photo…</div>
                ) : photoData?.photo ? (
                  <img src={photoData.photo} alt="Inspiration" className="w-full rounded-2xl border border-sakura-200" />
                ) : null}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, icon: Icon }) {
  return (
    <div className="card-soft p-5 flex items-center gap-4">
      <span className="h-12 w-12 grid place-items-center rounded-full bg-sakura-200 text-sakura-500"><Icon className="h-5 w-5" /></span>
      <div>
        <div className="text-xs uppercase tracking-wider font-bold text-sakura-500">{label}</div>
        <div className="font-serif text-2xl text-sakura-ink">{value}</div>
      </div>
    </div>
  );
}

function Row({ icon: Icon, label, value }) {
  return (
    <div className="rounded-2xl border border-sakura-200 p-3 bg-sakura-50">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider font-bold text-sakura-500"><Icon className="h-3.5 w-3.5" /> {label}</div>
      <div className="mt-0.5 text-sakura-ink">{value}</div>
    </div>
  );
}
